/* eslint-disable react/jsx-filename-extension */
import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import FeedPost from './FeedPost';
import TvPost from './TvPost';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import Title from '../../../../shared/_TBComponents/Title';

import { setUnPinToggleFalse } from '../../ducks/feedActions';
import useFeed from '../hooks/useFeed';

const FeedBoxTop = styled('div')`
  scroll-margin-top: ${(props) => (props.isTvMode ? '70px' : '320px')};
`;

const FeedContentContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${(props) => (props.isTvMode ? 'left' : 'center')};
  width: ${(props) => (props.isTvMode ? '100%' : '60%')};
  height: ${(props) => props.containerHeight};
  @media screen and (max-width: 640px) {
    width: 100%;
    max-width: 100%;
    min-width: 350px;
  }
`;

const SpinnerWrapper = styled('div')`
  position: absolute;
  display: flex;
  height: 50%;
  justify-content: center;
  width: 100%;
  margin-top: 300px;
`;

const TitleWrapper = styled('div')`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  align-items: center;
`;

const FeedBoxContent = ({ isTvMode, topRef, scrollToTopOfFeed }) => {
  const { handleFetchFeed, handleFetchGroups, handleFetchUsers } = useFeed();
  const isFeedLoading = useSelector((state) => state.feed.ui.isFeedLoading);
  const isPinnedFeedLoading = useSelector(
    (state) => state.feed.ui.isPinnedFeedLoading,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const feed = useSelector((state) => state.feed.data.feed);
  const pinnedFeed = useSelector((state) => state.feed.data.pinnedFeed);
  const unPinToggle = useSelector((state) => state.feed.data.unPinToggle);
  const isFeedShowing = useSelector((state) => state.feed.data.isFeedShowing);
  const selectedGroups = useSelector((state) => state.feed.data.selectedGroups);
  const searchString = useSelector((state) => state.feed.data.searchString);

  const getContainerHeight = useMemo(() => {
    const hasNoFeed = !feed || !Array.isArray(feed) || !feed.length;
    if (hasNoFeed && isTvMode) return '80vh';
    if (hasNoFeed && !isTvMode) return '70vh';
    return '100%';
  }, [feed, isTvMode]);

  const timeZone = currentUser?.userSettings?.timeZone;

  useEffect(() => {
    if (currentUser?.accountCode !== undefined) {
      handleFetchFeed(currentUser.accountCode);
      handleFetchGroups(currentUser.accountCode);
      handleFetchUsers(currentUser.accountCode);
    }
    if (unPinToggle) {
      setUnPinToggleFalse();
    }
  }, [currentUser, unPinToggle]);

  // update feed if group filter or search changes
  useEffect(() => {
    if (currentUser?.accountCode !== undefined) {
      handleFetchFeed(currentUser.accountCode);
      scrollToTopOfFeed();
    }
  }, [currentUser?.accountCode, JSON.stringify(selectedGroups), searchString]);

  const renderFeedPosts = useCallback((feedArray) => {
    // Defensive check: ensure feedArray is valid and has content
    if (!feedArray || !Array.isArray(feedArray) || !feedArray.length) {
      return [];
    }

    return feedArray.map((feedObject) => {
      // Defensive check: ensure feedObject has required properties
      if (!feedObject || !feedObject.id) {
        return null;
      }

      return isTvMode ? (
        <TvPost
          key={feedObject.id}
          feedObject={feedObject}
          timeZone={timeZone}
        />
      ) : (
        <FeedPost
          key={feedObject.id}
          feedObject={feedObject}
          timeZone={timeZone}
        />
      );
    }).filter(Boolean); // Remove any null entries from invalid objects
  }, [isTvMode, timeZone, feed, pinnedFeed]);

  const renderRecentPostsHeader = useMemo(() => {
    const shouldShowHeader = isFeedShowing && !isTvMode && !!pinnedFeed?.length && !!feed?.length;

    if (!shouldShowHeader) return null;
    return (
      <Text
        key='recent-posts-header'
        styledTextProps={{
          fontSize: 12,
          fontWeight: 900,
          letterSpacing: 1.3,
          marginBottom: '20px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        RECENT POSTS
      </Text>
    );
  }, [isFeedShowing, isTvMode, pinnedFeed?.length, feed?.length]);

  const renderFeedContent = useMemo(() => {
    const isLoading = isFeedLoading || isPinnedFeedLoading;
    // Defensive check: ensure feed is valid array before checking length
    const feedLength = feed && Array.isArray(feed) ? feed.length : 0;
    const pinnedFeedLength = pinnedFeed && Array.isArray(pinnedFeed) ? pinnedFeed.length : 0;
    const feedHasContent = !!feedLength || (isFeedShowing && !isTvMode && !!pinnedFeedLength);

    if (isLoading && !feedHasContent) {
      return isTvMode ? null : (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      );
    }
    if (!feedHasContent) {
      return (
        <TitleWrapper>
          <Title icon='feed'>No posts available</Title>
        </TitleWrapper>
      );
    }
    return (
      <>
        {renderRecentPostsHeader}
        {renderFeedPosts(feed)}
      </>
    );
  }, [
    isFeedLoading,
    isPinnedFeedLoading,
    feed,
    isFeedShowing,
    isTvMode,
    pinnedFeed,
    renderRecentPostsHeader,
    renderFeedPosts,
  ]);

  return (
    <>
      <FeedBoxTop ref={topRef} isTvMode={isTvMode} />
      <FeedContentContainer
        isTvMode={isTvMode}
        containerHeight={getContainerHeight}
      >
        {renderFeedContent}
      </FeedContentContainer>
    </>
  );
};

FeedBoxContent.propTypes = {
  isTvMode: PropTypes.bool.isRequired,
  topRef: PropTypes.instanceOf(Object).isRequired,
  scrollToTopOfFeed: PropTypes.func.isRequired,
};

export default FeedBoxContent;
